import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../Provider/AdminAuthProvider';
import { adminConfig } from '../config/AdminConfig';
import { FaUsers, FaPlus, FaTrash, FaShieldAlt } from 'react-icons/fa';
import AdminLayout from '../components/AdminLayout';

const AdminSettings = () => {
  const [adminEmails, setAdminEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const { addAdminEmail, removeAdminEmail, getUserEmail } = useAdminAuth();

  const loadConfig = async () => {
    try {
      setLoading(true);
      const config = await adminConfig.getAdminConfig();
      setAdminEmails(config.adminEmails || []);
    } catch (error) {
      showMessage('error', 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  // Load current configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      showMessage('error', 'Please enter an email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      const success = await addAdminEmail(newEmail.trim());
      
      if (success) {
        setAdminEmails([...adminEmails, newEmail.trim().toLowerCase()]);
        setNewEmail('');
        showMessage('success', 'Admin email added successfully');
      } else {
        showMessage('error', 'Email already exists in admin list');
      }
    } catch (error) {
      showMessage('error', 'Failed to add admin email');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    // Prevent removing own email
    if (email === getUserEmail()) {
      showMessage('error', 'You cannot remove your own admin email');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${email} from admin access?`)) {
      return;
    }

    try {
      setLoading(true);
      await removeAdminEmail(email);
      setAdminEmails(adminEmails.filter(e => e !== email));
      showMessage('success', 'Admin email removed successfully');
    } catch (error) {
      showMessage('error', 'Failed to remove admin email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Settings</h2>
          <p className="text-gray-600">Manage admin users and system settings</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 border border-green-200 text-green-800' 
              : 'bg-red-100 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Admin Users Management */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-6">
            <FaUsers className="text-orange-500 mr-3 text-xl" />
            <h3 className="text-xl font-semibold text-gray-800">Admin Users</h3>
          </div>

          {/* Add New Admin */}
          <form onSubmit={handleAddEmail} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add New Admin Email
            </label>
            <div className="flex">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@company.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white rounded-r-lg transition-colors flex items-center"
              >
                <FaPlus className="mr-1" />
                Add
              </button>
            </div>
          </form>

          {/* Current Admin List */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Current Admin Users</h4>
            <div className="space-y-2">
              {adminEmails.map((email, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FaShieldAlt className="text-orange-500 mr-3" />
                    <span className="text-sm text-gray-700">{email}</span>
                    {email === getUserEmail() && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        You
                      </span>
                    )}
                  </div>
                  
                  {email !== getUserEmail() && (
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      disabled={loading}
                      className="text-red-500 hover:text-red-700 disabled:text-red-300 transition-colors p-2"
                      title="Remove admin access"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
              
              {adminEmails.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  No admin users configured
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Security Information */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-lg font-medium text-blue-900 mb-3">Security Information</h4>
          <ul className="text-blue-800 text-sm space-y-2">
            <li>• This admin portal is environment-based and completely separate from the main application</li>
            <li>• Only add trusted email addresses to the admin list</li>
            <li>• Admin users must have valid Firebase authentication accounts</li>
            <li>• All admin actions are logged and can be audited</li>
            <li>• Remove admin access immediately when no longer needed</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;